'use client';

import { faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { easeInOut, motion } from 'framer-motion';
import Image from 'next/image';
import React from 'react';
import { Button } from '~/components/ui/Button';
import { cn } from '~/lib/cn';
import PixelCard from '../../AnimatedComponents/PixelCard';

interface Props {
    className?: string;
    title: string;
    src: string;
}

const containerVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: easeInOut
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.2,
            ease: easeInOut
        }
    }
};

const imageVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            duration: 0.2,
            ease: easeInOut
        }
    }
};

export const HeroSection: React.FC<React.PropsWithChildren<Props>> = ({ title, className, src, children }) => {
    return (
        <section
            className={cn(
                'tw-card-bordered tw-relative tw-my-5 tw-min-h-[400px] tw-overflow-hidden tw-p-0 md:tw-mb-10 md:tw-min-h-[500px]',
                className
            )}
        >
            <PixelCard colors="#38c172, #5cdb95, #82e7ad, #a8f3c6">
                <motion.div
                    className="tw-absolute tw-inset-2 tw-grid tw-grid-cols-1 tw-gap-6 md:tw-inset-6 md:tw-grid-cols-2"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ amount: 0.4, once: true }}
                >
                    {/* Left Column: Text  */}
                    <div className="tw-order-2 tw-flex tw-flex-col tw-justify-center tw-gap-2 md:tw-order-1 md:tw-gap-6">
                        <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-around tw-text-center">
                            <motion.div variants={itemVariants}>
                                <h1 className="tw-text-center tw-text-2xl tw-font-semibold tw-leading-tight tw-text-accents-9 sm:tw-text-4xl md:tw-text-5xl lg:tw-text-6xl">
                                    {title}
                                </h1>
                            </motion.div>

                            {children && (
                                <motion.div className="tw-hidden tw-text-accents-7 md:tw-block" variants={itemVariants}>
                                    {children}
                                </motion.div>
                            )}
                        </div>

                        <motion.div
                            variants={itemVariants}
                            className="tw-flex tw-flex-col tw-flex-wrap tw-items-center tw-justify-center tw-gap-3 sm:tw-flex-row"
                        >
                            <Button.Link href="mailto:sales@sportision.de" size="md" className="tw-w-full md:tw-w-fit">
                                <FontAwesomeIcon icon={faEnvelope} className="tw-mr-2" />
                                Angebot anfordern
                            </Button.Link>
                            <Button.Link
                                href="tel:029228751440"
                                color="secondary"
                                className="tw-w-full md:tw-w-fit"
                                flat
                                size="md"
                            >
                                <FontAwesomeIcon icon={faPhone} className="tw-mr-2" />
                                Beratung
                            </Button.Link>
                        </motion.div>
                    </div>

                    <motion.div
                        className="md:tw-min-h-auto tw-relative tw-min-h-[150px] tw-flex-1 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-white md:tw-order-2"
                        variants={imageVariants}
                    >
                        <Image
                            src={src}
                            alt={title}
                            priority
                            fill
                            quality={100}
                            sizes="(max-width: 1024px) 60vw, 90vw" // reduces image quality on some screens
                            fetchPriority="high"
                            className="tw-object-cover tw-object-left-top tw-shadow-sm tw-transition-shadow tw-duration-300"
                        />
                    </motion.div>
                </motion.div>
            </PixelCard>
        </section>
    );
};
